import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/login/data-access/auth.service';
import {SystemManagementService} from "../../../admin/data-access/system-management.service";
import {Message} from "../../../admin/feature/system-management/announcement-list/announcement-list.component";
import {faCheck, faChevronLeft, faChevronRight, faLock, faXmark} from "@fortawesome/free-solid-svg-icons";
import {Course} from "../../../admin/data-access/course.model";
import { StatusFormatterPipe } from './statusFormatter.pipe';
import { FullButtonComponent } from "../../../shared/ui/full-button/full-button.component";

export interface ExamPartScore {
  part_name: string
  part_score: number | null
  part_max_score: number | null
  part_passing_score: number | null
}

const OOP_PART_PASSING_SCORE = 12

export type PartScoreState = 'passed' | 'failed' | 'locked'

export interface StudentExam {
  course_id: number
  course_name: string
  exam_id: number
  exam_uuid: string
  exam_title: string
  attempts_max: number
  first_start: Date
  last_start: Date
  finish_by: Date
  status: string
  scored: boolean
  attempt_no: number
  start_time: Date
  answer_time: Date
  due_time: Date
  can_resume: boolean
  type?: string
  score?: number
  max_score?: number
  parts?: ExamPartScore[] | null
  // TODO started_late
  // TODO finished_late
}

@Component({
  selector: 'studentDashboard',
  templateUrl: './studentDashboard.html',
  providers: [StatusFormatterPipe, FullButtonComponent],
  styleUrls: ['studentDashboard.component.css'],
  standalone: false
})
export class StudentDashboardComponent implements OnInit, OnDestroy {
  statusFormatterPipe = inject(StatusFormatterPipe);
  router = inject(Router)
  headerDivStyle = `font-size: 1.875rem; line-height: 2.25rem; color: ${localStorage.getItem('primaryColor')}; display: flex; align-items: center;`
  primaryColor = localStorage.getItem('primaryColor')
  firstName: string = '';
  lastName: string = '';
  isLoading: boolean = true
  isLoadingList: boolean = true
  isLoadingListOptional: boolean = true
  examListResponse: StudentExam[] = []
  englishNBExamResponse?: StudentExam
  scoreBreakdownExam?: StudentExam
  optionalListResponse: Course[] = []
  orgID: number[] = []
  systemService = inject(SystemManagementService)
  studentMessages: Message[] = []
  studentMessageIter = 0
  messageBoxStyle = `background: linear-gradient(to left, transparent 10%, ${localStorage.getItem('primaryColor')}); border-radius: 8px; color: white; margin-top: 20px;`

  authService = inject(AuthService);

  // Turn into a signal when angular updates and they are available
  userSub: Subscription = this.authService.user$.subscribe((user) => {
    if (user) {
      this.firstName = user.given_name;
      this.lastName = user.family_name;
      this.isLoading = false;
    }
  });

  userPerms: Subscription = this.authService.userDashboard$.subscribe((user) => {
    if(user.success) {
      for(let perm of user.result) {
        // TODO push in a array to handle multiple orgs
        if(perm.organization_id != null && !this.orgID.includes(perm.organization_id) && perm.ui === 'ExamTake')
          this.orgID.push(perm.organization_id)
      }
      if(this.orgID.length > 0) {
        this.isLoadingList = true;
        for(let org of this.orgID) {
          this.authService.getMyExams(org).subscribe((response) => {
            if(response.success) {
              this.examListResponse = this.examListResponse.concat(response.result)
              if(this.hasExamByTitle('English (New Brunswick 2026)')) {
                this.englishNBExamResponse = this.examListResponse[this.getIndexByExamTitle('English (New Brunswick 2026)')]
                this.examListResponse.splice(this.getIndexByExamTitle('English (New Brunswick 2026)'), 1)
              }
              this.isLoadingList = false;
            } else {
              // TODO handle error
            }
          })
          this.authService.getOptionalExams(org).subscribe((response) => {
            if(response.success) {
              this.optionalListResponse = response.result
            } else {
              // TODO handle error
            }
            this.isLoadingListOptional = false
          })
        }
      }
    }
  })

  optIn() {

  }

  openScoreBreakdown(exam: StudentExam) {
    this.scoreBreakdownExam = exam
  }

  closeScoreBreakdown() {
    this.scoreBreakdownExam = undefined
  }

  // Intro and conclusion sections are not scored against a passing threshold
  getGradedParts(exam: StudentExam): ExamPartScore[] {
    return (exam.parts ?? []).filter(part => /^part\s*\d/i.test(part.part_name))
  }

  // The first part is the survey "Do you have prior programming experience in Java?" (yes/no). Answering no scores
  // below its passing score and the student never reaches Part 1
  hasNoJavaExperience(exam: StudentExam): boolean {
    const intro = exam.parts?.[0]
    return intro != null && intro.part_score != null && intro.part_score < (intro.part_passing_score ?? 0)
  }

  // NOTE The backend has no way of knowing the passing score for Part 5 - Object-Oriented Programming,
  // so it is hard coded here. Every other part uses part_passing_score from the API
  getPassingScore(part: ExamPartScore): number | null {
    if(/^part\s*5\b/i.test(part.part_name))
      return OOP_PART_PASSING_SCORE
    return part.part_passing_score
  }

  getPartState(part: ExamPartScore): PartScoreState {
    if(part.part_score == null || part.part_max_score == null)
      return 'locked'
    return part.part_score >= (this.getPassingScore(part) ?? 0) ? 'passed' : 'failed'
  }

  getPartsPassed(exam: StudentExam): number {
    return this.getGradedParts(exam).filter(part => this.getPartState(part) === 'passed').length
  }

  getPercent(value: number | null, max: number | null): number {
    if(value == null || !max)
      return 0
    return Math.min(100, Math.round(value / max * 100))
  }

  startExam(exam_id: number) {
    this.router.navigate(['dashboard/exam/take'], {queryParams: {id: exam_id}})
  }

  resumeExam(exam_uuid: string, exam_id: number) {
    // TODO examine how to find which part and question to resume the exam at
    this.router.navigate(['dashboard/exam/take'], {queryParams: {id: exam_id, uuid: exam_uuid, part: 0, resume: true}})
  }

  viewStudentGrades(exam_uuid: string) {
    this.router.navigate(['dashboard/exam/ViewGrade'], {queryParams: {exam_uuid: exam_uuid}})
  }

  ngOnInit() {
    this.systemService.listAnnouncements('student').subscribe((response) => {
      if(response.success) {
        this.studentMessages = []
        for(let message of response.result) {
          if(message.dashboard === 'student' || message.dashboard === 'universal') {
            this.studentMessages.push(message)
          }
        }
      } else {
        this.studentMessages = []
      }
    })
  }

  incrementMessages() {
    if(this.studentMessageIter < this.studentMessages.length)
      this.studentMessageIter++
  }

  decrementMessages() {
    if(this.studentMessageIter > 0)
      this.studentMessageIter--
  }

  ngOnDestroy() {
    this.userSub.unsubscribe();
    this.userPerms.unsubscribe()
  }

  hasExamByTitle(title: string) {
    return this.examListResponse.some(exam => exam.exam_title.toLocaleLowerCase() === title.toLocaleLowerCase());
  }

  getIndexByExamTitle(title: string) {
    return this.examListResponse.findIndex(exam => exam.exam_title.toLocaleLowerCase() === title.toLocaleLowerCase());
  }

  protected readonly faChevronRight = faChevronRight;
  protected readonly faChevronLeft = faChevronLeft;
  protected readonly faCheck = faCheck;
  protected readonly faXmark = faXmark;
  protected readonly faLock = faLock;
}
